import { distance, dot, normalize, subtract, wrap } from './math'
import type { Track, TrackProjection, TrackSample, Vec2 } from './types'

function pointAt(points: readonly Vec2[], index: number): Vec2 {
  const point = points[wrap(index, points.length)]
  if (!point) throw new Error('A closed track requires at least four control points.')
  return point
}

function catmullRomScalar(p0: number, p1: number, p2: number, p3: number, t: number): number {
  const t2 = t * t
  const t3 = t2 * t
  return 0.5 * (2 * p1 + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 + (-p0 + 3 * p1 - 3 * p2 + p3) * t3)
}

function catmullRomDerivative(p0: number, p1: number, p2: number, p3: number, t: number): number {
  const t2 = t * t
  return 0.5 * (-p0 + p2 + 2 * (2 * p0 - 5 * p1 + 4 * p2 - p3) * t + 3 * (-p0 + 3 * p1 - 3 * p2 + p3) * t2)
}

function evaluateSegment(points: readonly Vec2[], segment: number, t: number): { position: Vec2; tangent: Vec2 } {
  const p0 = pointAt(points, segment - 1)
  const p1 = pointAt(points, segment)
  const p2 = pointAt(points, segment + 1)
  const p3 = pointAt(points, segment + 2)
  const position = {
    x: catmullRomScalar(p0.x, p1.x, p2.x, p3.x, t),
    y: catmullRomScalar(p0.y, p1.y, p2.y, p3.y, t),
  }
  const tangent = normalize({
    x: catmullRomDerivative(p0.x, p1.x, p2.x, p3.x, t),
    y: catmullRomDerivative(p0.y, p1.y, p2.y, p3.y, t),
  })
  return { position, tangent }
}

export function createTrack(
  controlPoints: readonly Vec2[],
  sampleCount: number,
  checkpointCount: number,
  width: number,
): Track {
  if (controlPoints.length < 4) throw new Error('A closed track requires at least four control points.')
  if (sampleCount < checkpointCount || checkpointCount < 2) {
    throw new Error('Track sampling must provide at least one sample per checkpoint.')
  }

  const samples: TrackSample[] = []
  let cumulativeDistance = 0
  for (let index = 0; index < sampleCount; index += 1) {
    const trackTime = (index / sampleCount) * controlPoints.length
    const segment = Math.floor(trackTime)
    const localTime = trackTime - segment
    const { position, tangent } = evaluateSegment(controlPoints, segment, localTime)
    if (index > 0) cumulativeDistance += distance(samples[index - 1]?.position ?? position, position)
    samples.push({
      index,
      position,
      tangent,
      normal: { x: -tangent.y, y: tangent.x },
      distance: cumulativeDistance,
      width,
    })
  }

  const first = samples[0]
  const last = samples.at(-1)
  if (!first || !last) throw new Error('Track sampling produced no samples.')
  const totalLength = cumulativeDistance + distance(last.position, first.position)
  const checkpoints = Array.from({ length: checkpointCount }, (_, index) =>
    Math.floor((index * sampleCount) / checkpointCount),
  )

  return { samples, checkpoints, totalLength, width }
}

export function getTrackSample(track: Track, index: number): TrackSample {
  const sample = track.samples[wrap(index, track.samples.length)]
  if (!sample) throw new Error('Track has no samples.')
  return sample
}

export function projectToTrack(track: Track, point: Vec2, hintSampleIndex?: number): TrackProjection {
  let nearest = getTrackSample(track, 0)
  let nearestDistanceSquared = Number.POSITIVE_INFINITY

  const inspect = (sample: TrackSample) => {
    const offset = subtract(point, sample.position)
    const candidate = dot(offset, offset)
    if (candidate < nearestDistanceSquared) {
      nearest = sample
      nearestDistanceSquared = candidate
    }
  }

  if (hintSampleIndex === undefined || !Number.isFinite(hintSampleIndex)) {
    for (const sample of track.samples) inspect(sample)
  } else {
    const searchRadius = Math.max(12, Math.ceil(track.samples.length / 12))
    for (let offset = -searchRadius; offset <= searchRadius; offset += 1) {
      inspect(getTrackSample(track, hintSampleIndex + offset))
    }
    if (nearestDistanceSquared > (track.width * 4) ** 2) {
      nearestDistanceSquared = Number.POSITIVE_INFINITY
      for (const sample of track.samples) inspect(sample)
    }
  }

  const offset = subtract(point, nearest.position)
  return {
    sampleIndex: nearest.index,
    lateral: dot(offset, nearest.normal),
    progress: nearest.distance / track.totalLength,
    distance: nearest.distance,
  }
}

export function projectToTrackLocal(
  track: Track,
  point: Vec2,
  hintSampleIndex: number,
  searchRadius = 12,
): TrackProjection {
  let nearest = getTrackSample(track, hintSampleIndex)
  let nearestDistanceSquared = Number.POSITIVE_INFINITY
  const radius = Number.isFinite(searchRadius) ? Math.max(1, Math.floor(searchRadius)) : 12

  for (let sampleOffset = -radius; sampleOffset <= radius; sampleOffset += 1) {
    const sample = getTrackSample(track, hintSampleIndex + sampleOffset)
    const offset = subtract(point, sample.position)
    const candidate = dot(offset, offset)
    if (candidate < nearestDistanceSquared) {
      nearest = sample
      nearestDistanceSquared = candidate
    }
  }

  const offset = subtract(point, nearest.position)
  return {
    sampleIndex: nearest.index,
    lateral: dot(offset, nearest.normal),
    progress: nearest.distance / track.totalLength,
    distance: nearest.distance,
  }
}
